import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Luggage } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import FareTermsIcons from './FareTermsIcons';

/**
 * Optional "Fare & Bags" section for the commercial flight form (PLAN-fare-terms.md §3.3).
 *
 * Every field starts as Unknown, which is stored as null and never shown on the
 * card. Any edit marks the terms as entered manually, which outranks Duffel and
 * parsed data. Clearing every field removes the terms.
 */

const BAG_OPTIONS = [
  { label: 'Unknown', value: null },
  { label: 'None', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
];

const CHANGE_OPTIONS = [
  { label: 'Unknown', value: null },
  { label: 'Allowed', value: { allowed: true } },
  { label: 'Not allowed', value: { allowed: false } },
];

const CANCEL_OPTIONS = [
  { label: 'Unknown', value: null },
  { label: 'Refundable', value: { allowed: true, outcome: 'cash_refund' } },
  { label: 'Credit only', value: { allowed: true, outcome: 'travel_credit' } },
  { label: 'Non-refundable', value: { allowed: false, outcome: 'none' } },
];

const isKnown = (value) => value !== null && value !== undefined && value !== '';

const hasAnyTerms = (terms) => Boolean(
  terms && (
    isKnown(terms.fare_brand) || isKnown(terms.fare_basis_code) || isKnown(terms.booking_class)
    || isKnown(terms.carry_on?.quantity) || isKnown(terms.checked?.quantity)
    || isKnown(terms.change?.allowed) || isKnown(terms.cancel?.allowed)
  )
);

const Segmented = ({ label, options, isSelected, onSelect }) => (
  <div role="group" aria-label={label} className="flex flex-wrap gap-1">
    {options.map((option) => {
      const selected = isSelected(option.value);
      return (
        <button
          key={option.label}
          type="button"
          aria-pressed={selected}
          onClick={() => onSelect(option.value)}
          className={`px-2.5 py-1 text-xs rounded-md border focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-blue-line ${
            selected
              ? 'bg-ui-primary text-ui-on-primary border-transparent'
              : 'bg-ui-surface border-ui-control text-ui-ink-2 hover:bg-ui-hover'
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const FeeInputs = ({ idPrefix, label, penalty, onChange }) => (
  <div className="flex items-center gap-2 mt-2">
    <Label htmlFor={`${idPrefix}-fee`} className="text-xs text-ui-muted w-10">Fee</Label>
    <Input
      id={`${idPrefix}-fee`}
      aria-label={`${label} fee`}
      inputMode="decimal"
      placeholder="0.00"
      value={penalty.fee_amount ?? ''}
      onChange={(e) => onChange({ fee_amount: e.target.value.trim() || null })}
      className="h-8 w-28 text-sm"
    />
    <Input
      aria-label={`${label} fee currency`}
      placeholder="USD"
      maxLength={3}
      value={penalty.fee_currency ?? ''}
      onChange={(e) => onChange({ fee_currency: e.target.value.trim().toUpperCase() || null })}
      className="h-8 w-16 text-sm uppercase"
    />
  </div>
);

const FareTermsFormSection = ({ value, onChange, disabled = false }) => {
  const terms = value || {};
  const [open, setOpen] = useState(false);

  const update = (patch) => {
    const next = {
      ...terms,
      carry_on: { ...(terms.carry_on || {}) },
      checked: { ...(terms.checked || {}) },
      change: { ...(terms.change || {}) },
      cancel: { ...(terms.cancel || {}) },
      ...patch,
      source: 'manual',
      source_detail: null,
      captured_at: new Date().toISOString(),
    };
    onChange(hasAnyTerms(next) ? next : null);
  };

  const setText = (field) => (e) => update({ [field]: e.target.value.trim() ? e.target.value : null });
  const setBag = (field) => (quantity) => update({
    [field]: quantity === null ? {} : { ...(terms[field] || {}), quantity },
  });
  const setPenalty = (field) => (choice) => update({
    // A fee only applies while the change or cancellation is allowed.
    [field]: choice === null ? {} : { ...(choice.allowed ? terms[field] || {} : {}), outcome: null, ...choice },
  });
  const patchPenalty = (field) => (patch) => {
    const current = terms[field] || {};
    const merged = { ...current, ...patch };
    // A fee needs a currency; default to USD rather than store a bare number.
    if (merged.fee_amount && !merged.fee_currency) merged.fee_currency = 'USD';
    if (!merged.fee_amount && !patch.fee_currency) merged.fee_currency = null;
    update({ [field]: merged });
  };

  const penaltyMatches = (penalty) => (option) => {
    if (option === null) return !isKnown(penalty?.allowed);
    if (penalty?.allowed !== option.allowed) return false;
    if (!option.outcome) return true;
    // Duffel says only "allowed"; an allowed cancel with no outcome reads as Refundable.
    if (!penalty.outcome) return option.outcome === 'cash_refund' || option.outcome === 'none';
    return penalty.outcome === option.outcome;
  };

  return (
    <div className="p-4 bg-ui-sunk rounded-xl border border-ui-line">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          className="flex-1 text-lg font-semibold flex items-center gap-2 text-left text-ui-ink"
        >
          {open ? <ChevronDown className="w-4 h-4 text-ui-muted" /> : <ChevronRight className="w-4 h-4 text-ui-muted" />}
          <Luggage className="w-5 h-5 text-ui-muted" />
          Fare &amp; Bags
          <span className="text-xs font-normal text-ui-faint">Optional</span>
        </button>
        {!open && hasAnyTerms(value) && <FareTermsIcons terms={value} />}
      </div>

      {open && (
        <fieldset disabled={disabled} className="mt-4 space-y-4 disabled:opacity-60">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="fare-brand" className="text-xs text-ui-muted">Fare brand</Label>
              <Input id="fare-brand" placeholder="Main Cabin" value={terms.fare_brand ?? ''} onChange={setText('fare_brand')} className="h-8 text-sm" />
            </div>
            <div>
              <Label htmlFor="fare-basis" className="text-xs text-ui-muted">Fare basis</Label>
              <Input id="fare-basis" placeholder="VAVOA0BQ" value={terms.fare_basis_code ?? ''} onChange={setText('fare_basis_code')} className="h-8 text-sm uppercase" />
            </div>
            <div>
              <Label htmlFor="booking-class" className="text-xs text-ui-muted">Booking class</Label>
              <Input id="booking-class" placeholder="V" maxLength={2} value={terms.booking_class ?? ''} onChange={setText('booking_class')} className="h-8 text-sm uppercase" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-medium text-ui-ink-2 mb-1.5">Carry-on</div>
              <Segmented
                label="Carry-on bags"
                options={BAG_OPTIONS}
                isSelected={(v) => (v === null ? !isKnown(terms.carry_on?.quantity) : terms.carry_on?.quantity === v)}
                onSelect={setBag('carry_on')}
              />
            </div>
            <div>
              <div className="text-xs font-medium text-ui-ink-2 mb-1.5">Checked bags</div>
              <Segmented
                label="Checked bags"
                options={BAG_OPTIONS}
                isSelected={(v) => (v === null ? !isKnown(terms.checked?.quantity) : terms.checked?.quantity === v)}
                onSelect={setBag('checked')}
              />
              {terms.checked?.quantity > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <Label htmlFor="checked-weight" className="text-xs text-ui-muted w-10">Max</Label>
                  <Input
                    id="checked-weight"
                    aria-label="Checked bag weight in kg"
                    inputMode="decimal"
                    placeholder="23"
                    value={terms.checked?.weight_kg ?? ''}
                    onChange={(e) => {
                      const kg = parseFloat(e.target.value);
                      update({ checked: { ...terms.checked, weight_kg: Number.isFinite(kg) ? kg : null } });
                    }}
                    className="h-8 w-20 text-sm"
                  />
                  <span className="text-xs text-ui-muted">kg each</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-medium text-ui-ink-2 mb-1.5">Changes</div>
              <Segmented
                label="Changes"
                options={CHANGE_OPTIONS}
                isSelected={penaltyMatches(terms.change)}
                onSelect={setPenalty('change')}
              />
              {terms.change?.allowed === true && (
                <FeeInputs idPrefix="change" label="Change" penalty={terms.change} onChange={patchPenalty('change')} />
              )}
            </div>
            <div>
              <div className="text-xs font-medium text-ui-ink-2 mb-1.5">Cancellation</div>
              <Segmented
                label="Cancellation"
                options={CANCEL_OPTIONS}
                isSelected={penaltyMatches(terms.cancel)}
                onSelect={setPenalty('cancel')}
              />
              {terms.cancel?.allowed === true && (
                <FeeInputs idPrefix="cancel" label="Cancellation" penalty={terms.cancel} onChange={patchPenalty('cancel')} />
              )}
            </div>
          </div>

          {value && (
            <div className="flex items-center justify-between pt-2 border-t border-ui-divider text-xs text-ui-faint">
              <span>{value.source === 'manual' ? 'Entered manually' : value.source_detail || 'From booking'}</span>
              <button type="button" onClick={() => onChange(null)} className="text-ui-link hover:underline">
                Clear fare &amp; bags
              </button>
            </div>
          )}
        </fieldset>
      )}
    </div>
  );
};

export default FareTermsFormSection;
